/**
 * Normalisation + présentation des options persistées sur CommandeProduit.options.
 * Snapshot = vérité. Ne jamais dériver count depuis la quantité produit.
 * Affichage uniquement — aucun recalcul de pricing.
 *
 * Cardinalité (NOUVEAUX snapshots) : type / choixMultiple / min / max / nbInclus / obligatoire.
 * Snapshots legacy sans cardinalité : fallback d'affichage qty-based uniquement
 * (ne pas déduire CHOIX_UNIQUE / CHOIX_MULTIPLE depuis quantity seule).
 */

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function parseOptionsRaw(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function readCount(choice) {
  if (!choice || typeof choice !== 'object') return null;
  if (choice.count != null && choice.count !== '') {
    const n = Number(choice.count);
    return Number.isFinite(n) ? Math.max(0, n) : null;
  }
  if (choice.quantite != null && choice.quantite !== '') {
    const n = Number(choice.quantite);
    return Number.isFinite(n) ? Math.max(0, n) : null;
  }
  return null;
}

function readUnitPrice(choice) {
  return roundMoney(Number(choice?.prix ?? choice?.prixSupplement ?? choice?.price) || 0);
}

/** Groupes génériques : ne jamais masquer le nom du choix (ex. Wasabi). */
const GENERIC_GROUP_RE = /^(suppl[ée]ments?|extras?|options?|autres?)$/i;

function sameFrLabel(a, b) {
  return String(a).localeCompare(String(b), 'fr', { sensitivity: 'accent' }) === 0;
}

function containsFr(haystack, needle) {
  return String(haystack)
    .toLocaleLowerCase('fr')
    .includes(String(needle).toLocaleLowerCase('fr'));
}

/**
 * Libellé humain canonique — snapshot uniquement (compact / legacy UI).
 */
function choiceDisplayName(groupName, choiceName) {
  const choice = choiceName && String(choiceName).trim() ? String(choiceName).trim() : null;
  const group = groupName && String(groupName).trim() ? String(groupName).trim() : null;

  if (!choice && !group) return 'Option';
  if (!choice) return group;
  if (!group) return choice;

  if (GENERIC_GROUP_RE.test(group)) return choice;
  if (sameFrLabel(group, choice)) return choice;
  if (containsFr(group, choice) && group.length >= choice.length) return group;

  const prefixMatch = group.match(/^(sauce|option|options)(?:\s+(.+))?$/i);
  if (prefixMatch) {
    const base = (prefixMatch[2] || '').trim();
    if (!base) return choice;
    if (sameFrLabel(base, choice)) return choice;
    if (containsFr(base, choice) && base.length >= choice.length) return base;
    const choiceJoined =
      choice.charAt(0).toLocaleLowerCase('fr') + choice.slice(1);
    return `${base} ${choiceJoined}`;
  }

  const choiceJoined =
    choice.charAt(0).toLocaleLowerCase('fr') + choice.slice(1);
  return `${group} ${choiceJoined}`;
}

/**
 * @returns {'CHOIX_UNIQUE'|'CHOIX_MULTIPLE'|null}
 */
function resolveOptionCardinality(source) {
  if (!source || typeof source !== 'object') return null;
  const typeRaw = source.type != null ? String(source.type).trim() : '';
  if (typeRaw === 'CHOIX_MULTIPLE' || typeRaw === 'multi') return 'CHOIX_MULTIPLE';
  if (typeRaw === 'CHOIX_UNIQUE' || typeRaw === 'single') return 'CHOIX_UNIQUE';
  if (source.choixMultiple === true) return 'CHOIX_MULTIPLE';
  if (source.choixMultiple === false) return 'CHOIX_UNIQUE';
  return null;
}

function pickCardinalityMeta(source) {
  if (!source || typeof source !== 'object') {
    return {
      type: null,
      choixMultiple: null,
      choixMinimum: null,
      choixMaximum: null,
      nbInclus: null,
      obligatoire: null,
      cardinality: null,
    };
  }
  const cardinality = resolveOptionCardinality(source);
  const choixMinimum =
    source.choixMinimum != null && source.choixMinimum !== ''
      ? Number(source.choixMinimum)
      : null;
  const choixMaximum =
    source.choixMaximum != null && source.choixMaximum !== ''
      ? Number(source.choixMaximum)
      : null;
  const nbInclus =
    source.nbInclus != null && source.nbInclus !== '' ? Number(source.nbInclus) : null;
  return {
    type:
      cardinality ||
      (source.type != null && String(source.type).trim()
        ? String(source.type).trim()
        : null),
    choixMultiple:
      typeof source.choixMultiple === 'boolean'
        ? source.choixMultiple
        : cardinality === 'CHOIX_MULTIPLE'
          ? true
          : cardinality === 'CHOIX_UNIQUE'
            ? false
            : null,
    choixMinimum: Number.isFinite(choixMinimum) ? choixMinimum : null,
    choixMaximum: Number.isFinite(choixMaximum) ? choixMaximum : null,
    nbInclus: Number.isFinite(nbInclus) ? nbInclus : null,
    obligatoire:
      typeof source.obligatoire === 'boolean' ? source.obligatoire : null,
    cardinality,
  };
}

/**
 * Libellé choix pour ticket / lignes (séparateur qty configurable).
 * CHOIX_UNIQUE → jamais de quantité.
 * CHOIX_MULTIPLE / legacy → quantité seulement si > 1.
 */
function formatOptionChoiceQtyLabel(nom, qty, cardinality, { qtySep = ' x ' } = {}) {
  const name = String(nom || '').trim();
  if (!name) return '';
  const n = Math.max(1, Number(qty) || 1);
  if (cardinality === 'CHOIX_UNIQUE') return name;
  if (n > 1) return `${n}${qtySep}${name}`;
  return name;
}

function buildChoiceRow(groupName, choice, meta) {
  const choiceName =
    choice.nom || choice.label || choice.name || choice.libelle || choice.value || null;
  if (choiceName == null && !groupName) return null;

  let count = readCount(choice);
  if (count == null) count = 1;
  if (count <= 0) return null;

  const unitPrice = readUnitPrice(choice);
  const total = roundMoney(unitPrice * count);
  const displayName = choiceDisplayName(groupName, choiceName);
  const cardinality = meta?.cardinality ?? null;

  return {
    groupName: groupName || null,
    groupId: meta?.groupId || null,
    choiceId: choice.id || choice.choixId || null,
    choiceName: choiceName ? String(choiceName).trim() : displayName,
    displayName,
    count,
    unitPrice,
    total,
    type: meta?.type ?? null,
    choixMultiple: meta?.choixMultiple ?? null,
    choixMinimum: meta?.choixMinimum ?? null,
    choixMaximum: meta?.choixMaximum ?? null,
    nbInclus: meta?.nbInclus ?? null,
    obligatoire: meta?.obligatoire ?? null,
    cardinality,
  };
}

function isCompositionGroup(name, id) {
  return /ingrédient|compos/i.test(String(name || id || ''));
}

/**
 * Groupes normalisés avec cardinalité (si présente dans le snapshot).
 */
function normalizeOrderOptionGroups(raw) {
  const value = parseOptionsRaw(raw);
  if (value == null) return [];

  const groups = [];

  if (Array.isArray(value)) {
    const byKey = new Map();
    for (const item of value) {
      if (!item || typeof item !== 'object') continue;
      const groupName = item.optionNom || item.groupName || item.nomGroupe || null;
      const groupId = item.optionId || item.groupId || null;
      if (isCompositionGroup(groupName, groupId)) continue;
      const key = String(groupId || groupName || 'Option');
      if (!byKey.has(key)) {
        const meta = pickCardinalityMeta(item);
        byKey.set(key, {
          groupId: groupId || null,
          groupName: groupName || 'Option',
          ...meta,
          choices: [],
        });
      } else {
        const existing = byKey.get(key);
        if (existing.cardinality == null) {
          const meta = pickCardinalityMeta(item);
          if (meta.cardinality != null) {
            Object.assign(existing, meta);
          }
        }
      }
      const g = byKey.get(key);
      const row = buildChoiceRow(g.groupName, item, g);
      if (row) g.choices.push(row);
    }
    for (const g of byKey.values()) {
      if (g.choices.length) groups.push(g);
    }
    return groups;
  }

  if (typeof value === 'object') {
    for (const [key, group] of Object.entries(value)) {
      if (!group || typeof group !== 'object') continue;
      const groupName = group.nom || group.optionNom || null;
      const groupId = group.optionId || key || null;
      if (isCompositionGroup(groupName, groupId)) continue;
      const meta = {
        ...pickCardinalityMeta(group),
        groupId: group.optionId || (key && !/^\d+$/.test(key) ? key : null),
      };
      const choices = [];
      if (Array.isArray(group.choix)) {
        for (const choice of group.choix) {
          const row = buildChoiceRow(groupName, choice, meta);
          if (row) choices.push(row);
        }
      } else {
        const row = buildChoiceRow(groupName, group, meta);
        if (row) choices.push(row);
      }
      if (choices.length) {
        groups.push({
          groupId: meta.groupId,
          groupName: groupName || 'Option',
          type: meta.type,
          choixMultiple: meta.choixMultiple,
          choixMinimum: meta.choixMinimum,
          choixMaximum: meta.choixMaximum,
          nbInclus: meta.nbInclus,
          obligatoire: meta.obligatoire,
          cardinality: meta.cardinality,
          choices,
        });
      }
    }
  }

  return groups;
}

function pushChoice(rows, { groupName, choice, meta }) {
  const row = buildChoiceRow(groupName, choice, meta || pickCardinalityMeta(choice));
  if (row) rows.push(row);
}

/**
 * @param {unknown} raw — CommandeProduit.options (objet | JSON string | tableau)
 */
function normalizeOrderOptionChoices(raw) {
  return normalizeOrderOptionGroups(raw).flatMap((g) => g.choices);
}

/**
 * Lignes métier pour modal / UI :
 * - CHOIX_UNIQUE : « Wasabi : Sans »
 * - CHOIX_MULTIPLE qty 1 : « Suppléments : Avocat, Fromage »
 * - CHOIX_MULTIPLE qty>1 : « Sauce : 2 × Sauce soja sucrée »
 * - Legacy : qty<=1 sans « 1 × », qty>1 avec quantité
 */
function formatOrderOptionBusinessLines(raw) {
  const groups = normalizeOrderOptionGroups(raw);
  const lines = [];

  for (const group of groups) {
    const groupName = String(group.groupName || 'Option').trim() || 'Option';
    const choices = group.choices || [];
    if (!choices.length) continue;
    const card = group.cardinality;

    if (card === 'CHOIX_UNIQUE') {
      lines.push(
        `${groupName} : ${formatOptionChoiceQtyLabel(
          choices[0].choiceName,
          choices[0].count,
          'CHOIX_UNIQUE',
          { qtySep: ' × ' }
        )}`
      );
      continue;
    }

    const formatParts = (cardinality) =>
      choices.map((c) =>
        formatOptionChoiceQtyLabel(c.choiceName, c.count, cardinality, { qtySep: ' × ' })
      );

    if (card === 'CHOIX_MULTIPLE') {
      if (choices.length === 1) {
        lines.push(`${groupName} : ${formatParts('CHOIX_MULTIPLE')[0]}`);
      } else if (choices.every((c) => c.count <= 1)) {
        lines.push(`${groupName} : ${formatParts('CHOIX_MULTIPLE').join(', ')}`);
      } else {
        lines.push(`${groupName} : ${formatParts('CHOIX_MULTIPLE').join(', ')}`);
      }
      continue;
    }

    // Legacy — pas de déduction de type depuis quantity seule
    if (choices.length === 1) {
      lines.push(`${groupName} : ${formatParts(null)[0]}`);
    } else if (choices.every((c) => c.count <= 1)) {
      lines.push(`${groupName} : ${formatParts(null).join(', ')}`);
    } else {
      lines.push(`${groupName} : ${formatParts(null).join(', ')}`);
    }
  }

  return lines;
}

/**
 * Lignes ticket Sunmi (structure groupe inline / multi-lignes).
 * Utilise « x » ASCII (pas ×) pour rester aligné ESC/POS existant.
 */
function formatOrderOptionTicketGroupLines(raw) {
  const groups = normalizeOrderOptionGroups(raw);
  const lines = [];

  for (const group of groups) {
    const groupName = String(group.groupName || 'Option').trim() || 'Option';
    const choices = (group.choices || []).filter((c) => c && c.choiceName);
    if (!choices.length) continue;
    const card = group.cardinality;

    if (choices.length === 1) {
      lines.push(
        `${groupName} : ${formatOptionChoiceQtyLabel(
          choices[0].choiceName,
          choices[0].count,
          card,
          { qtySep: ' x ' }
        )}`
      );
      continue;
    }

    lines.push(`${groupName} :`);
    for (const c of choices) {
      lines.push(
        `- ${formatOptionChoiceQtyLabel(c.choiceName, c.count, card, { qtySep: ' x ' })}`
      );
    }
  }

  return lines;
}

function formatMoneyFr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

/** Libellé compact : Wasabi ×2 (+0,40 €) — qty seulement si > 1 */
function formatOrderOptionCompactLabel(row, { moneyFn = formatMoneyFr } = {}) {
  if (!row || !row.displayName) return null;
  let label = String(row.displayName);
  const showQty =
    row.cardinality === 'CHOIX_UNIQUE' ? false : Number(row.count) > 1;
  if (showQty) label += ` ×${row.count}`;
  if (row.total > 0.009) label += ` (+${moneyFn(row.total)})`;
  return label;
}

function formatOrderOptionCompactLabels(raw, opts) {
  return normalizeOrderOptionChoices(raw)
    .map((row) => formatOrderOptionCompactLabel(row, opts))
    .filter(Boolean);
}

/**
 * Meta cardinalité à figer dans un snapshot de groupe (écriture nouvelles commandes).
 * Source = option catalogue ou payload panier déjà enrichi.
 */
function buildSnapshotCardinalityFields(option) {
  if (!option || typeof option !== 'object') return {};
  const cardinality = resolveOptionCardinality(option);
  const multi =
    cardinality === 'CHOIX_MULTIPLE'
      ? true
      : cardinality === 'CHOIX_UNIQUE'
        ? false
        : typeof option.choixMultiple === 'boolean'
          ? option.choixMultiple
          : null;

  const out = {};
  const type =
    cardinality ||
    (multi === true ? 'CHOIX_MULTIPLE' : multi === false ? 'CHOIX_UNIQUE' : null);
  if (type) out.type = type;
  if (typeof multi === 'boolean') out.choixMultiple = multi;
  if (option.choixMinimum != null && option.choixMinimum !== '') {
    const n = Number(option.choixMinimum);
    if (Number.isFinite(n)) out.choixMinimum = n;
  }
  if (option.choixMaximum != null && option.choixMaximum !== '') {
    const n = Number(option.choixMaximum);
    if (Number.isFinite(n)) out.choixMaximum = n;
  }
  if (option.nbInclus != null && option.nbInclus !== '') {
    const n = Number(option.nbInclus);
    if (Number.isFinite(n)) out.nbInclus = n;
  }
  if (typeof option.obligatoire === 'boolean') out.obligatoire = option.obligatoire;
  return out;
}

export {
  normalizeOrderOptionChoices,
  normalizeOrderOptionGroups,
  formatOrderOptionCompactLabel,
  formatOrderOptionCompactLabels,
  formatOrderOptionBusinessLines,
  formatOrderOptionTicketGroupLines,
  formatOptionChoiceQtyLabel,
  resolveOptionCardinality,
  buildSnapshotCardinalityFields,
  parseOptionsRaw,
  roundMoney,
};